/**
 * Dagre Layout Module
 *
 * This module applies the Dagre graph layout algorithm to position nodes in the FlowNodes canvas.
 *
 * Architecture:
 * - Groups nodes by parentId (nested flows) and applies layout independently to each group
 * - Positions resource nodes (sources, datasets, sinks) using Dagre's automatic layout
 * - Repositions special nodes (credentials, nexset loaders) relative to their parent nodes
 * - Positions secondary flows (linked/triggered flows) below the main flow
 *
 * Key Concepts:
 * - Resource nodes: Nodes that should be laid out by Dagre (sources, datasets, sinks, etc.)
 * - Special nodes: Nodes positioned relative to others (credentials, nexset loaders)
 * - Secondary flows: Linked/triggered flows displayed below main flow
 *
 * See docs/FlowNodesArchitecture.md for detailed architecture documentation.
 * See docs/FlowNodesModificationGuide.md for modification instructions.
 */

import type { Edge, Node } from '@xyflow/react';
import type { LayoutDirection } from './types';
import { Position } from '@xyflow/react';
import dagre from 'dagre';
import { FLOW_TYPE_CUSTOM, FLOW_TYPE_RAG } from 'js/constants/pipelineConstants';
import { getConnectionOrVendorType, isSqlAutomation } from 'js/lib/ResourceUtils';
import {
  shouldRenderWebhookAction,
  shouldRenderExternalTriggerAction,
  shouldRenderCronAction,
  shouldRenderRunAction,
} from '../cards/components/ExpandActions/utils/shouldRenderSourceAction';
import {
  FLOW_CANVAS_CONFIG,
  getDagreSpacingForFlowType,
  getDirectionConfig,
  getRankSpacingForFlowType,
  isScheduleCanvasNodeType,
  LAYOUT,
  rankdirToLayoutDirection,
} from './constants';
import {
  EXPANDED_ACTIONS_WIDTH,
  NODE_HEIGHT,
  NODE_WIDTH,
  DATA_RETRIEVER_MIN_WIDTH,
} from './consts';
import { adjustSharedDatasetPositions } from './sharedDatasetHelpers';

/**
 * Set of node types that should be laid out by the Dagre algorithm.
 *
 * These are resource nodes (sources, datasets, sinks) that need automatic positioning.
 * If you add a new resource node type, add it here so it gets laid out automatically.
 *
 * Note: Special nodes (credentials, nexset loaders) are positioned manually after Dagre layout.
 */
const RESOURCE_NODE_TYPES = new Set([
  'resource',
  'data_source',
  'data_set',
  'data_sink',
  'splitter',
  // RAG resource-like nodes
  'query_prompt',
  'data_retriever_holder',
  'query_rewriter',
  'context_enricher',
  'llm_processor',
  'reranker',
  'query_response',
  // API Server flow nodes
  'api_server',
  'api_request',
  'api_success_modifier',
  'splitter',
  'splitter_output',
  'api_target',
  'api_result',
  'api_error',
  'api_error_modifier',
  // Nexset loader
  'nexsetLoader',
  // BYO (custom) flow synthetic nodes — laid out inline source → code → sink
  'code',
  // BYO create-flow virtual SOURCE shell (before a real source is configured)
  'byoSourceShell',
  // BYO destination shell (before a sink is attached)
  'byoDestShell',
]);

/**
 * Supported layout directions for the flow canvas.
 * LR = Left-to-Right. TB = Top-to-Bottom. RL = Right-to-Left.
 */
export type RankDir = 'LR' | 'RL' | 'TB';

/**
 * Options for Dagre layout algorithm
 */
type DagreLayoutOptions = {
  isRagFlow?: boolean; // Whether this is a RAG flow (affects layout behavior)
  flowType?: string; // Flow type (e.g., 'streaming', 'replication', 'cdc', 'elt', 'spark', 'in_memory', 'custom', 'rag', 'api_server')
  vendors: Record<string, any>; // Vendor information for node sizing
  metaInfoById: Record<string, any>; // Metadata for connection categories
  rankdir?: RankDir; // Layout direction: LR (default), TB, or RL
  byoV2Enabled?: boolean; // Gates BYO (custom) v2 layout special-casing
};

/**
 * Checks if a node is a resource node that should be laid out by Dagre.
 *
 * @param node - The node to check
 * @returns true if the node type is in RESOURCE_NODE_TYPES
 */
function isResourceNode(node: Node): boolean {
  return RESOURCE_NODE_TYPES.has(node.type as string);
}

// Visible dimensions of the rendered resource (source/dataset/sink) card.
// The dagre bounding box for these nodes is NODE_WIDTH x NODE_HEIGHT
// (300x250), but ResourceCard actually renders at max-width 232px and is
// height: fit-content — typically around 140px tall. Because the card is
// top/left-aligned inside the bounding box, using the bounding-box center
// for positioning satellites (schedule controls, credential) visibly
// skews them right / down. We use these as first-render fallbacks before
// React Flow populates `node.measured`.
const VISIBLE_RESOURCE_CARD_WIDTH = 232;
const VISIBLE_RESOURCE_CARD_HEIGHT = 140;

/** Minimum top padding (in TB) below the flow-summary overlay header. */
const TB_CANVAS_TOP_PADDING = 60;

/**
 * Calculates the size (width and height) of a node for Dagre layout.
 *
 * Node sizes are important for Dagre to calculate proper spacing and positioning.
 *
 * Special handling:
 * - data_source nodes: Width includes EXPANDED_ACTIONS_WIDTH if expand actions are shown
 * - Other nodes: Uses data.width/height if available, otherwise defaults to NODE_WIDTH/NODE_HEIGHT
 *
 * @param node - The node to calculate size for
 * @param vendors - Vendor information (used for data_source nodes)
 * @param metaInfoById - Metadata for connection categories (used for data_source nodes)
 * @returns Object with width and height in pixels
 *
 * @see EXPANDED_ACTIONS_WIDTH - Additional width for sources with expand actions
 * @see NODE_WIDTH, NODE_HEIGHT - Default node dimensions
 */
function getNodeSize(node: Node, vendors: Record<string, any>, metaInfoById: Record<string, any>) {
  if (node.type === 'data_source') {
    const source = node.data?.item;
    const canvasScheduleActive = !!(node.data as any)?.showScheduleCanvasHandles;
    const connectionOrVendorType = getConnectionOrVendorType(
      source,
      vendors,
      'source_type',
      'vendor_id',
    );
    const connectionCategory = isSqlAutomation(source, vendors)
      ? metaInfoById['sql_runner']?.connectionCategory
      : metaInfoById[connectionOrVendorType]?.connectionCategory;

    const hasExpandStrip =
      shouldRenderWebhookAction(source) ||
      shouldRenderExternalTriggerAction(source) ||
      (!canvasScheduleActive &&
        (shouldRenderCronAction(source, connectionCategory) ||
          shouldRenderRunAction(source, connectionCategory)));

    return {
      width: NODE_WIDTH + (hasExpandStrip ? EXPANDED_ACTIONS_WIDTH : 0),
      height: (node?.data?.height as number) || NODE_HEIGHT,
    };
  }

  // Special handling for data_retriever_holder nodes
  // Use the actual width for layout because:
  // 1. Children inside the retriever holder have parentId, so they're in a separate group
  // 2. Edges connect from the container's boundary, which needs the actual width
  // 3. The container's width doesn't affect main flow spacing since children are grouped separately
  // 4. Width is now synchronized: empty state uses DATA_RETRIEVER_MIN_WIDTH (350px)
  if (node.type === 'data_retriever_holder') {
    const actualWidth = (node?.data?.width as number) || DATA_RETRIEVER_MIN_WIDTH;

    return {
      width: actualWidth, // Use actual width for layout (now synchronized with display)
      height: (node?.data?.height as number) || NODE_HEIGHT,
    };
  }

  // Special handling for nexsetLoader nodes
  // NexsetLoaderNode has a fixed width of 400px (see NexsetLoaderNode.tsx:60)
  // This is the "Scanning Source for Nexsets..." synthetic element
  if (node.type === 'nexsetLoader') {
    const NEXSET_LOADER_WIDTH = 400; // Fixed width from NexsetLoaderNode component
    return {
      width: NEXSET_LOADER_WIDTH,
      height: (node?.data?.height as number) || NODE_HEIGHT,
    };
  }

  // =========================================================================
  // API Server Flow Node Dimensions
  // =========================================================================
  // These dimensions MUST match the actual rendered sizes from SCSS files:
  // - ApiServer.module.scss: .container { min-width: 256px; min-height: 144px; }
  // - Request.module.scss: .container { min-width: 256px; min-height: 144px; }
  // - Target.module.scss: .container { min-width: 256px; min-height: 144px; }
  // - Result.module.scss: .container { min-width: 256px; } (no min-height, ~100px)
  // - Error.module.scss: .container { min-width: 256px; } (no min-height, ~100px)
  //
  // Using accurate dimensions is CRITICAL for proper Dagre layout calculation.
  // =========================================================================
  const API_SERVER_DIMENSIONS = {
    // Main API Server node: base (256px) + method tabs sidebar (80px)
    api_server: { width: 336, height: 144 },
    // Request nodes: standard width, full height
    api_request: { width: 256, height: 144 },
    // Target nodes: standard width, full height
    api_target: { width: 256, height: 144 },
    // Transform/modifier nodes
    api_success_modifier: { width: 256, height: 144 },
    splitter_output: { width: 256, height: 144 },
    splitter: { width: 256, height: 100 },
    api_error_modifier: { width: 256, height: 100 },
    // Result/Error nodes: standard width, shorter height (no min-height in CSS)
    api_result: { width: 256, height: 100 },
    api_error: { width: 256, height: 100 },
  } as const;

  const apiServerDimension = API_SERVER_DIMENSIONS[node.type as keyof typeof API_SERVER_DIMENSIONS];
  if (apiServerDimension) {
    return {
      width: (node?.data?.width as number) || apiServerDimension.width,
      height: (node?.data?.height as number) || apiServerDimension.height,
    };
  }

  const width = (node?.data?.width as number) || NODE_WIDTH;
  const height = (node?.data?.height as number) || NODE_HEIGHT;
  return { width, height };
}

// Per-rank spacing configuration lives in constants.ts
// (RANK_SPACING_BY_FLOW_TYPE + getRankSpacingForFlowType), split by LR/TB.

// Extra horizontal spacing after a sink, if next node is webhook response nexset.
const DATA_SINK_WEBHOOK_RANK_SPACING = 80;

/**
 * Layout configuration – derived from the centralized FLOW_CANVAS_CONFIG.shared.
 * Uses a spread so existing `LAYOUT_CONFIG.xxx` references remain untouched.
 */
const LAYOUT_CONFIG = {
  defaultRankdir: 'LR' as const,
  retrieverHolderNodesep: 30,
  ...FLOW_CANVAS_CONFIG.shared,
} as const;

/**
 * Returns React Flow handle positions for the given layout direction.
 * LR: source right, target left. RL: source left, target right.
 * TB: source bottom, target top (flow runs top to bottom).
 */
function getHandlePositionsForRankDir(rankdir: RankDir): {
  sourcePosition: (typeof Position)[keyof typeof Position];
  targetPosition: (typeof Position)[keyof typeof Position];
} {
  if (rankdir === 'RL') {
    return { sourcePosition: Position.Left, targetPosition: Position.Right };
  }
  if (rankdir === 'TB') {
    return { sourcePosition: Position.Bottom, targetPosition: Position.Top };
  }
  return { sourcePosition: Position.Right, targetPosition: Position.Left };
}

/**
 * Groups resource nodes by their parentId for independent layout processing.
 *
 * This allows nested flows to have independent layouts while maintaining
 * their relative positions. Nodes without a parentId are grouped as '__root__'.
 *
 * @param nodes - All nodes to group
 * @returns Map of parentId -> array of resource nodes in that group
 */
function groupNodesByParentId(nodes: Node[]): Record<string, Node[]> {
  return nodes
    .filter((n) => isResourceNode(n))
    .reduce<Record<string, Node[]>>((acc, n) => {
      const key = (n.parentId as string) || '__root__';
      if (!acc[key]) acc[key] = [];
      acc[key].push(n);
      return acc;
    }, {});
}

/**
 * Applies Dagre layout to a single group of nodes.
 *
 * For each group (nested flow or root), this function:
 * 1. Calculates the group's base offset (top-left corner)
 * 2. Creates a Dagre graph with node sizes and edges
 * 3. Runs the layout algorithm
 * 4. Converts Dagre coordinates to React Flow coordinates
 *
 * @param groupNodes - Nodes in this group
 * @param parentId - The parent ID for this group ('__root__' for root nodes)
 * @param allNodes - All nodes (used to check parent types)
 * @param edges - All edges
 * @param options - Layout options
 */
function applyDagreLayoutToGroup(
  groupNodes: Node[],
  parentId: string,
  allNodes: Node[],
  edges: Edge[],
  options: DagreLayoutOptions & {
    nodesep: number;
    ranksep: number;
    edgesep: number;
  },
): void {
  if (groupNodes.length === 0) return;

  const { isRagFlow = false, vendors, metaInfoById, nodesep, ranksep, edgesep } = options;

  // Check if this group is children inside a retriever holder
  const isRetrieverHolderChildren =
    parentId !== '__root__' &&
    allNodes.find((n) => n.id === parentId)?.type === 'data_retriever_holder';

  // Calculate base offset (preserve existing position unless retriever holder children)
  let baseX = Math.min(...groupNodes.map((n) => n.position.x));
  let baseY = Math.min(...groupNodes.map((n) => n.position.y));

  if (isRetrieverHolderChildren) {
    baseX = 0;
    baseY = 0;
  }

  const groupNodesep = isRetrieverHolderChildren ? LAYOUT_CONFIG.retrieverHolderNodesep : nodesep;

  const rankdir = options.rankdir ?? LAYOUT_CONFIG.defaultRankdir;

  // Create Dagre graph
  const g = new dagre.graphlib.Graph();
  g.setGraph({
    rankdir,
    nodesep: groupNodesep,
    ranksep: ranksep,
    edgesep: edgesep,
    marginx: 20,
    marginy: 20,
  });
  g.setDefaultEdgeLabel(() => ({}));

  // Add nodes with their sizes
  groupNodes.forEach((n) => {
    const { width, height } = getNodeSize(n, vendors, metaInfoById);
    g.setNode(n.id, { width, height });
  });

  // Add edges connecting nodes within this group
  const nodeIds = new Set(groupNodes.map((n) => n.id));
  const withinEdges: { source: string; target: string }[] = [];
  edges.forEach((e) => {
    if (nodeIds.has(e.source) && nodeIds.has(e.target)) {
      withinEdges.push({ source: e.source, target: e.target });
      g.setEdge(e.source, e.target);
    }
  });

  // Skip layout if no edges (unless RAG flow)
  if (withinEdges.length === 0 && !isRagFlow) {
    return;
  }

  // Run Dagre layout algorithm
  dagre.layout(g);

  // Convert Dagre coordinates to React Flow coordinates
  groupNodes.forEach((n) => {
    const graphNode = g.node(n.id);
    if (!graphNode) return;

    const { width, height } = getNodeSize(n, vendors, metaInfoById);
    const dagreX = graphNode.x; // Dagre's center X coordinate
    const dagreY = graphNode.y; // Dagre's center Y coordinate

    // React Flow expects top-left coordinates, Dagre gives center coordinates
    n.position = {
      x: baseX + (dagreX - width / 2),
      y: baseY + (dagreY - height / 2),
    };
  });
}

const getDescendants = (parent: Node, nodes: Node[]): Node[] => {
  const nodesByFlowNodeId = nodes.reduce(
    (acc, node) => {
      const flowNodeId = (node?.data?.item as any)?.flow_node_id;
      if (flowNodeId) {
        acc[flowNodeId] = node;
      }
      return acc;
    },
    {} as Record<string, Node>,
  );
  const getNodeDescendants = (node: Node): Node[] => {
    if (!node?.data?.item) {
      return [];
    }
    const descendants = [];
    (node.data.item as any).children?.forEach((childId: string) => {
      const child = nodesByFlowNodeId[childId];
      if (child) {
        descendants.push(child, ...getNodeDescendants(child));
      }
    });

    return descendants;
  };

  return getNodeDescendants(parent);
};

/**
 * Adjusts nodes following data_retriever_holder containers for fixed edge length.
 *
 * The data_retriever_holder is a container - children inside have parentId and are
 * in a separate group. Edges connect from the container's boundary.
 *
 * To keep edges a fixed small length, we adjust nodes that come after the retriever
 * holder to be positioned right after the container's right edge with standard spacing.
 *
 * @param nodes - All nodes (will be mutated)
 * @param options - Layout options
 */
function adjustNodesAfterRetrieverHolders(nodes: Node[], options: DagreLayoutOptions): void {
  const { vendors, metaInfoById } = options;
  const retrieverHolder = nodes.find((n) => n.type === 'data_retriever_holder');

  if (!retrieverHolder) return;

  // figure out the width of the blue data retriever holder rectangle
  const childNodes = nodes.filter((n) => n.parentId === retrieverHolder.id);

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  let runtimeWidth = DATA_RETRIEVER_MIN_WIDTH;

  if (childNodes.length > 0) {
    childNodes.forEach((childNode) => {
      if (!childNode) return;

      const { width, height } = getNodeSize(childNode, vendors, metaInfoById);
      let nodeX = childNode.position.x;
      let nodeY = childNode.position.y;

      // Special handling for nexsetLoader nodes: They're positioned later
      if (
        childNode.type === 'nexsetLoader' &&
        (childNode.id as string).startsWith('nexset-loader-')
      ) {
        const flowNodeId = (childNode.id as string).replace('nexset-loader-', '');
        const sourceNode =
          childNodes.find((n) => n.id === `node-${flowNodeId}`) ||
          nodes.find((n) => n.id === `node-${flowNodeId}`);
        if (sourceNode) {
          const sourceWidth = (sourceNode.data?.width as number) || NODE_WIDTH;
          nodeX = sourceNode.position.x + sourceWidth;
          nodeY = sourceNode.position.y;
        }
      }

      // Account for 40px padding (matching getRelativeNodesBounds behavior)
      minX = Math.min(minX, nodeX - 20);
      maxX = Math.max(maxX, nodeX + width + 20);
      minY = Math.min(minY, nodeY - 20);
      maxY = Math.max(maxY, nodeY + height + 20);
    });

    if (minX !== Infinity) {
      const rectWidth = maxX - minX;
      runtimeWidth = Math.max(rectWidth, DATA_RETRIEVER_MIN_WIDTH);
    }
  }

  const layoutDir = rankdirToLayoutDirection(options.rankdir);
  const isTB = options.rankdir === 'TB';
  const spacing = getDagreSpacingForFlowType(options.flowType, layoutDir);
  const standardSpacing = spacing.nodesep;

  // Update node.data.width with runtime width so it persists
  const retrieverData = retrieverHolder.data as any;
  const storedWidth = retrieverData?.width as number;
  if (runtimeWidth !== storedWidth) {
    retrieverData.width = runtimeWidth;
  }

  const followingNodes = getDescendants(retrieverHolder, nodes);

  if (isTB) {
    // TB: position following nodes below the container
    const runtimeHeight = (retrieverData?.height as number) || NODE_HEIGHT;
    const containerBottomEdge = retrieverHolder.position.y + runtimeHeight;
    const desiredY = containerBottomEdge + standardSpacing;
    const offset = desiredY - (followingNodes[0]?.position?.y ?? 0);
    followingNodes.forEach((targetNode) => {
      if (
        targetNode?.position?.y !== undefined &&
        !targetNode.parentId &&
        RESOURCE_NODE_TYPES.has(targetNode.type)
      ) {
        targetNode.position.y += offset;
      }
    });
  } else {
    // LR: position following nodes to the right of the container
    const containerRightEdge = retrieverHolder.position.x + runtimeWidth;
    const desiredX = containerRightEdge + standardSpacing;
    const offset = desiredX - (followingNodes[0]?.position?.x ?? 0);
    followingNodes.forEach((targetNode) => {
      if (
        targetNode?.position?.x !== undefined &&
        !targetNode.parentId &&
        RESOURCE_NODE_TYPES.has(targetNode.type)
      ) {
        targetNode.position.x += offset;
      }
    });
  }
}

// Credential spacing now driven by FLOW_CANVAS_CONFIG (constants.ts)

/**
 * Repositions credential nodes relative to their source nodes.
 *
 * LR: Below the source, offset right by expand-actions width.
 * TB: To the LEFT of the source, vertically centred.
 *
 * In TB, when a credential's source is inside a flow container
 * (dependent / linked flow), naively placing the credential at
 * `source.x - credWidth - gap` produces a NEGATIVE X relative to the
 * container origin — and the container's minWidth calc in FlowNode.tsx
 * only grows the right edge, so the credential renders OUTSIDE the
 * container border. To keep the credential inside, we mirror
 * `positionFlowControlNodes`: shift every sibling inside the affected
 * container right by `credWidth + gap` before positioning the
 * credential, freeing up a left margin for it.
 *
 * ID format: 'credential-{flowNodeId}'
 */
function repositionCredentialNodes(nodes: Node[], options: DagreLayoutOptions): void {
  const { isRagFlow = false, vendors, metaInfoById, rankdir } = options;
  const dirCfg = getDirectionConfig(rankdir as LayoutDirection);
  const isTB = rankdir === 'TB';

  const credentialNodes = nodes.filter((n) => (n.id as string).startsWith('credential-'));

  // PHASE 1 (TB only): for any credential whose source is inside a flow
  // container, shift every sibling in that container right by
  // `credWidth + gap` so the credential has room to sit on the left while
  // staying within the container's bounds. Track per-parent so multiple
  // credentials in the same container only shift once.
  if (isTB) {
    const adjustedParents = new Set<string>();
    credentialNodes.forEach((credNode) => {
      const flowNodeId = (credNode.id as string).replace('credential-', '');
      const sourceNode = nodes.find((n) => n.id === `node-${flowNodeId}`);
      if (!sourceNode?.parentId) return;
      if (adjustedParents.has(sourceNode.parentId)) return;
      adjustedParents.add(sourceNode.parentId);

      const credWidth = (credNode.data as any)?.width || NODE_WIDTH;
      const shiftAmount = credWidth + dirCfg.credential.gap;

      const siblings = nodes.filter(
        (n) =>
          n.parentId === sourceNode.parentId &&
          // Don't double-shift the credential itself; its position is
          // computed from the (already-shifted) source in PHASE 2.
          !(n.id as string).startsWith('credential-'),
      );
      siblings.forEach((n) => {
        n.position.x += shiftAmount;
      });
    });
  }

  // PHASE 2: position each credential relative to its (possibly-shifted)
  // source.
  credentialNodes.forEach((credNode) => {
    const flowNodeId = (credNode.id as string).replace('credential-', '');
    const sourceNode = nodes.find((n) => n.id === `node-${flowNodeId}`);
    if (!sourceNode) return;

    const source = sourceNode.data?.item;
    const { width: sourceWidth, height: sourceHeight } = getNodeSize(
      sourceNode,
      vendors,
      metaInfoById,
    );

    const credWidth = (credNode.data as any)?.width || NODE_WIDTH;
    const credHeight = (credNode.data as any)?.height || dirCfg.credential.heightEstimate;

    if (isTB) {
      // TB: credential to the LEFT of the source. Align centers on
      // the source's *visible* center — ResourceCard is left-aligned
      // inside a taller bounding box, so measured height can't be
      // trusted for the visible center. Use VISIBLE_RESOURCE_CARD_HEIGHT.
      const credMeasured = (credNode as any).measured;
      const visualCredHeight = (credMeasured?.height as number) || credHeight;
      const sourceCenterY = sourceNode.position.y + VISIBLE_RESOURCE_CARD_HEIGHT / 2;
      credNode.position = {
        x: sourceNode.position.x - credWidth - dirCfg.credential.gap + 30,
        y: sourceCenterY - visualCredHeight / 2,
      };
      return;
    }

    // LR: below the source, offset right
    const canvasScheduleActive = !!(sourceNode.data as any)?.showScheduleCanvasHandles;
    const connectionOrVendorType = getConnectionOrVendorType(
      source,
      vendors,
      'source_type',
      'vendor_id',
    );
    const connectionCategory = isSqlAutomation(source, vendors)
      ? metaInfoById['sql_runner']?.connectionCategory
      : metaInfoById[connectionOrVendorType]?.connectionCategory;
    const hasExpandStrip =
      !isRagFlow &&
      (shouldRenderWebhookAction(source) ||
        shouldRenderExternalTriggerAction(source) ||
        (!canvasScheduleActive &&
          (shouldRenderCronAction(source, connectionCategory) ||
            shouldRenderRunAction(source, connectionCategory))));
    const xOffset =
      LAYOUT.CREDENTIAL_HORIZONTAL_OFFSET + (hasExpandStrip ? EXPANDED_ACTIONS_WIDTH : 0);
    const yOffset = NODE_HEIGHT / LAYOUT.CREDENTIAL_VERTICAL_DIVISOR;

    credNode.position = {
      x: sourceNode.position.x + xOffset,
      y: sourceNode.position.y + yOffset,
    };
  });
}

/**
 * Repositions nexset loader nodes relative to their source nodes.
 *
 * Nexset loaders are displayed when a source has pending nexsets to load.
 * They are positioned:
 * - Horizontally: To the right of the source node with spacing (40px for RAG flows, 200px for others)
 * - Vertically: Aligned with the top of the source node
 *
 * ID format: 'nexset-loader-{flowNodeId}'
 *
 * @param nodes - All nodes (will be mutated)
 * @param options - Layout options containing flow type information
 */
function repositionNexsetLoaderNodes(nodes: Node[], options: DagreLayoutOptions): void {
  const NEXSET_LOADER_SPACING = options?.flowType === FLOW_TYPE_RAG ? -40 : 200;
  const isTB = options.rankdir === 'TB';
  const isByo = options?.flowType === FLOW_TYPE_CUSTOM && !!options?.byoV2Enabled;

  nodes
    .filter((n) => (n.id as string).startsWith('nexset-loader-'))
    .forEach((loaderNode) => {
      const flowNodeId = (loaderNode.id as string).replace('nexset-loader-', '');
      const sourceNode = nodes.find((n) => n.id === `node-${flowNodeId}`);
      if (!sourceNode) return;

      const sourceWidth = (sourceNode.data?.width as number) || NODE_WIDTH;
      const sourceHeight = (sourceNode.data?.height as number) || NODE_HEIGHT;

      if (isByo && !isTB) {
        // BYO (LR): place loader BELOW the source so it doesn't overlap canvas nodes
        loaderNode.position = {
          x: sourceNode.position.x,
          y: sourceNode.position.y + VISIBLE_RESOURCE_CARD_HEIGHT + 20,
        };
      } else if (isTB) {
        loaderNode.position = {
          x: sourceNode.position.x,
          y: sourceNode.position.y + sourceHeight + NEXSET_LOADER_SPACING,
        };
      } else {
        loaderNode.position = {
          x: sourceNode.position.x + sourceWidth + NEXSET_LOADER_SPACING,
          y: sourceNode.position.y,
        };
      }
    });
}

/**
 * Repositions flow control nodes (Run Now, Cron) relative to their SOURCE node.
 * LR: to the LEFT of the source (stacked vertically, Run Now above Cron).
 * TB: on TOP of the source (side by side, Run Now left, Cron right).
 */
function positionFlowControlNodes(nodes: Node[], options: DagreLayoutOptions): void {
  const rankdir = options.rankdir ?? LAYOUT_CONFIG.defaultRankdir;
  const dirCfg = getDirectionConfig(rankdir as LayoutDirection);
  const isTB = rankdir === 'TB';
  const { height, gap, widthEstimate, controlNodeSpacing } = dirCfg.flowControl;

  const controlNodes = nodes.filter((n) => isScheduleCanvasNodeType(n.type));
  const bySource = controlNodes.reduce(
    (acc, n) => {
      const sourceNodeId = (n.data as any)?.sourceNodeId;
      if (!sourceNodeId) return acc;
      if (!acc[sourceNodeId]) acc[sourceNodeId] = [];
      acc[sourceNodeId].push(n);
      return acc;
    },
    {} as Record<string, Node[]>,
  );

  // PHASE 1: For sources inside a container, shift ALL siblings down/right
  // to make room for the control nodes BEFORE positioning them.
  const adjustedParents = new Set<string>();
  Object.entries(bySource).forEach(([sourceNodeId]) => {
    const sourceNode = nodes.find((n) => n.id === sourceNodeId);
    if (!sourceNode?.parentId) return;
    adjustedParents.add(sourceNode.parentId);
  });

  if (isTB) {
    // TB: shift all children in affected containers DOWN to make room above
    const shiftAmount = height + gap;
    adjustedParents.forEach((parentId) => {
      const siblings = nodes.filter((n) => n.parentId === parentId);
      siblings.forEach((n) => {
        n.position.y += shiftAmount;
      });
    });
  } else {
    // LR: shift all children in affected containers RIGHT to make room on left
    const shiftAmount = widthEstimate + gap;
    adjustedParents.forEach((parentId) => {
      const siblings = nodes.filter((n) => n.parentId === parentId);
      siblings.forEach((n) => {
        n.position.x += shiftAmount;
      });
    });
  }

  // PHASE 2: Position control nodes relative to (shifted) source
  Object.entries(bySource).forEach(([sourceNodeId, controlList]) => {
    const sourceNode = nodes.find((n) => n.id === sourceNodeId);
    if (!sourceNode) return;

    // Sort: Cron Job first (top/left), Run Now second (bottom/right)
    const sorted = [...controlList].sort((a, b) => {
      const aCron = (a.id as string).startsWith('cron-') ? 0 : 1;
      const bCron = (b.id as string).startsWith('cron-') ? 0 : 1;
      return aCron - bCron;
    });

    // Both TB (paired side-by-side above source) and LR (stacked left of
    // source) want the schedule-control ICONS visually aligned across
    // siblings — cron's clock at the same Y as run-now's play in TB, and
    // cron's clock at the same X as run-now's play in LR. The icon sits at
    // the TOP of each node's content (`align-items: center` column flex),
    // and the badge + subtitle text below have variable height/width based
    // on the date string. So per-node measured dimensions differ and any
    // per-node positioning that anchors on the BOTTOM (TB) or LEFT (LR)
    // floats the icons apart. Use the MAX measured dimension across all
    // siblings of this source to anchor every node off the same reference.
    const maxCtrlHeight = Math.max(
      ...sorted.map((n) => ((n as any).measured?.height as number) || height),
    );
    const maxCtrlWidth = Math.max(
      ...sorted.map((n) => ((n as any).measured?.width as number) || widthEstimate),
    );

    if (isTB) {
      // TB: side by side ABOVE the source. Align centers so the
      // vertical "imaginary line" passes through the source card's
      // *visible* center. ResourceCard renders at max-width 232px,
      // left-aligned inside a 300px React Flow bounding box, so
      // `node.measured.width` (300) is NOT the visible center — always
      // use VISIBLE_RESOURCE_CARD_WIDTH for the source's visible width.
      const sourceCenterX = sourceNode.position.x + VISIBLE_RESOURCE_CARD_WIDTH / 2;

      sorted.forEach((controlNode, idx) => {
        const ctrlMeasured = (controlNode as any).measured;
        const ctrlWidth = (ctrlMeasured?.width as number) || widthEstimate;

        // Place each control node relative to sourceCenterX.
        // Solo: centered on the source column.
        // Paired: offset left/right so the two nodes flank the column
        // with `controlNodeSpacing` between their inner edges.
        let centerX: number;
        if (sorted.length === 1) {
          centerX = sourceCenterX;
        } else {
          const half = ctrlWidth / 2 + controlNodeSpacing / 2;
          centerX = idx === 0 ? sourceCenterX - half : sourceCenterX + half;
        }

        // Anchor every sibling at the same TOP Y — derived from the
        // tallest sibling's height — so the icons (which sit at the top
        // of each card's content) line up. Otherwise a cron with
        // multi-line subtitle ("On Destination Completion / …") floats
        // higher than a run-now with single-line "Last Run / <date>".
        controlNode.position = {
          x: centerX - ctrlWidth / 2,
          y: sourceNode.position.y - maxCtrlHeight - gap,
        };
      });
    } else {
      // LR: stacked vertically to the LEFT of source. Anchor every
      // sibling so its ICON sits on the same X line — a horizontal
      // shift would betray that cron and run-now don't visually
      // belong to the same column.
      //
      // Each control node's content uses `align-items: center` column
      // flex, so the icon is at width/2 of the node. Setting node.x =
      // (target icon X) - thisWidth/2 lines up icons across siblings
      // regardless of subText min/max-width drift (150-170px).
      const sourceCenterY = sourceNode.position.y + VISIBLE_RESOURCE_CARD_HEIGHT / 2;
      const totalHeight = sorted.length * height + (sorted.length - 1) * controlNodeSpacing;
      const startY = sourceCenterY - totalHeight / 2;
      // Place icons on a vertical line at this X — left of source by
      // `gap + maxCtrlWidth/2` so the widest sibling's right edge sits
      // `gap` from the source.
      const iconColumnX = sourceNode.position.x - gap - maxCtrlWidth / 2;
      sorted.forEach((controlNode, idx) => {
        const ctrlMeasured = (controlNode as any).measured;
        const ctrlWidth = (ctrlMeasured?.width as number) || widthEstimate;
        const ctrlHeight = (ctrlMeasured?.height as number) || height;
        controlNode.position = {
          x: iconColumnX - ctrlWidth / 2,
          y: startY + idx * (height + controlNodeSpacing) + (height - ctrlHeight) / 2,
        };
      });
    }
  });
}

/**
 * In TB, the flow-summary header ("Nexset Detected / Transformed / …")
 * is rendered as a canvas overlay. Child nodes of each flow container
 * are positioned relative to the container's top; anything with a
 * negative/small Y (e.g. cron or run-now sitting above the source)
 * ends up clipped behind the overlay.
 *
 * Shift each flow's children down so the top-most child clears the
 * overlay. No-op in LR (schedule nodes sit beside the source, not above).
 */
function ensureTopPaddingPerFlow(nodes: Node[], options: DagreLayoutOptions): void {
  if (options.rankdir !== 'TB') return;

  const childrenByParent = nodes.reduce<Record<string, Node[]>>((acc, n) => {
    if (!n.parentId) return acc;
    if (!acc[n.parentId]) acc[n.parentId] = [];
    acc[n.parentId].push(n);
    return acc;
  }, {});

  Object.values(childrenByParent).forEach((siblings) => {
    const minY = Math.min(...siblings.map((n) => n.position.y));
    const delta = TB_CANVAS_TOP_PADDING - minY;
    if (delta > 0) {
      siblings.forEach((n) => {
        n.position.y += delta;
      });
    }
  });

  // Also cover root-level resource nodes (flows without a parent container).
  const rootResourceNodes = nodes.filter((n) => !n.parentId && isResourceNode(n));
  if (rootResourceNodes.length > 0) {
    const minY = Math.min(...rootResourceNodes.map((n) => n.position.y));
    const delta = TB_CANVAS_TOP_PADDING - minY;
    if (delta > 0) {
      rootResourceNodes.forEach((n) => {
        n.position.y += delta;
      });
    }
  }
}

/**
 * Positions secondary flows below their parent flows.
 *
 * Secondary flows (linked flows and triggered flows) are displayed below
 * their specific parent flow, not below all flows combined.
 *
 * @param nodes - All nodes (will be mutated)
 * @param edges - All edges
 */
function positionSecondaryFlows(nodes: Node[], edges: Edge[]): void {
  const secondaryFlowSummaryNodes = nodes.filter(({ type }) => type === 'secondary_flow');

  if (secondaryFlowSummaryNodes.length === 0) return;

  const secondaryFlowNodes = nodes.filter(
    ({ data, type }) => data?.isSecondaryFlow && type !== 'secondary_flow',
  );
  const flowNodes = nodes.filter(({ type }) => type === 'flow');

  // Build maps of parent flow -> secondary flows using edges
  const parentToSecondaryFlows = new Map<string, string[]>();
  const secondaryToParentFlows = new Map<string, string[]>();
  const flowNodeMap = new Map(flowNodes.map((n) => [n.id, n]));

  secondaryFlowSummaryNodes.forEach((secondaryFlow) => {
    const incomingEdges = edges.filter(
      (edge) => edge.target === secondaryFlow.id && edge.source.startsWith('flow-'),
    );
    const outgoingEdges = edges.filter(
      (edge) => edge.source === secondaryFlow.id && edge.target.startsWith('flow-'),
    );

    const parentFlowIds = new Set<string>();

    incomingEdges.forEach((edge) => {
      if (flowNodeMap.has(edge.source)) {
        parentFlowIds.add(edge.source);
      }
    });

    outgoingEdges.forEach((edge) => {
      if (flowNodeMap.has(edge.target)) {
        parentFlowIds.add(edge.target);
      }
    });

    if (parentFlowIds.size > 0) {
      const validParentFlowIds = Array.from(parentFlowIds);
      secondaryToParentFlows.set(secondaryFlow.id, validParentFlowIds);

      validParentFlowIds.forEach((parentFlowId) => {
        if (!parentToSecondaryFlows.has(parentFlowId)) {
          parentToSecondaryFlows.set(parentFlowId, []);
        }
        parentToSecondaryFlows.get(parentFlowId)!.push(secondaryFlow.id);
      });
    }
  });

  // Helper to calculate the bottom of a flow
  const calculateFlowBottom = (flowId: string): number => {
    const parentFlow = flowNodeMap.get(flowId);
    if (!parentFlow) return 0;

    const parentFlowChildren = nodes.filter(
      (n) => n.parentId === flowId && !n.data?.isSecondaryFlow,
    );
    const allParentFlowNodes = [parentFlow, ...parentFlowChildren];

    if (allParentFlowNodes.length === 0) {
      const parentHeight = (parentFlow.data?.height as number) || NODE_HEIGHT;
      return (parentFlow.position?.y ?? 0) + parentHeight;
    }

    const bottoms = allParentFlowNodes.map((n) => {
      const nodeY = n.position?.y ?? 0;
      const nodeHeight = (n.data?.height as number) || NODE_HEIGHT;
      return nodeY + nodeHeight;
    });

    return Math.max(...bottoms);
  };

  // Position each secondary flow below its parent(s)
  secondaryFlowSummaryNodes.forEach((secondaryFlowNode, globalIndex) => {
    const parentFlowIds = secondaryToParentFlows.get(secondaryFlowNode.id) || [];

    let flowBottom: number;
    let primaryParentFlowId: string | null = null;

    if (parentFlowIds.length > 0) {
      let maxBottom = -Infinity;
      let validParentFound = false;

      for (const parentFlowId of parentFlowIds) {
        const parentFlow = flowNodeMap.get(parentFlowId);
        if (parentFlow && typeof parentFlow.position?.y === 'number') {
          const bottom = calculateFlowBottom(parentFlowId);
          if (bottom > maxBottom && isFinite(bottom)) {
            maxBottom = bottom;
            primaryParentFlowId = parentFlowId;
            validParentFound = true;
          }
        }
      }

      if (validParentFound && maxBottom !== -Infinity && isFinite(maxBottom)) {
        flowBottom = maxBottom + LAYOUT_CONFIG.secondaryFlowMargin;
      } else {
        flowBottom = LAYOUT_CONFIG.secondaryFlowMargin;
      }
    } else {
      const allNonSecondaryNodes = nodes.filter(
        ({ data, position }) => !data?.isSecondaryFlow && typeof position?.y === 'number',
      );

      if (allNonSecondaryNodes.length > 0) {
        const bottoms = allNonSecondaryNodes.map((n) => {
          const nodeY = n.position.y;
          const nodeHeight = (n.data?.height as number) || NODE_HEIGHT;
          return nodeY + nodeHeight;
        });
        const maxBottom = Math.max(...bottoms);
        flowBottom = isFinite(maxBottom)
          ? maxBottom + LAYOUT_CONFIG.secondaryFlowMargin
          : LAYOUT_CONFIG.secondaryFlowMargin;
      } else {
        flowBottom = LAYOUT_CONFIG.secondaryFlowMargin;
      }
    }

    const siblingSecondaryFlows = primaryParentFlowId
      ? parentToSecondaryFlows.get(primaryParentFlowId) || []
      : [];
    const siblingIndex = siblingSecondaryFlows.indexOf(secondaryFlowNode.id);
    const effectiveIndex = siblingIndex >= 0 ? siblingIndex : globalIndex;

    const indexOffset = effectiveIndex * LAYOUT_CONFIG.secondaryFlowHeight;
    const finalY = flowBottom + indexOffset;

    // Offset siblings horizontally so connecting edges don't overlap
    if (effectiveIndex > 0) {
      secondaryFlowNode.position.x += effectiveIndex * 40;
    }

    if (isFinite(finalY) && finalY >= 0) {
      secondaryFlowNode.position.y = finalY;
    } else {
      secondaryFlowNode.position.y =
        LAYOUT_CONFIG.secondaryFlowMargin + globalIndex * LAYOUT_CONFIG.secondaryFlowHeight;
    }
  });

  // Position secondary flow content nodes (when expanded)
  secondaryFlowNodes.forEach(({ position }) => {
    position.y += LAYOUT_CONFIG.secondaryFlowHeight + LAYOUT_CONFIG.secondaryFlowPadding;
    position.x += LAYOUT_CONFIG.secondaryFlowPadding;
  });
}

/**
 * Positions multiple independent flows horizontally (expanded view).
 *
 * When showing multiple flows in the same canvas (like in project expanded view),
 * we need to arrange them horizontally (side by side) to prevent overlap.
 *
 * @param nodes - All nodes (will be mutated)
 * @param options - Layout options
 */
function positionIndependentFlows(nodes: Node[], options: DagreLayoutOptions): void {
  const { vendors, metaInfoById } = options;
  const independentFlowNodes = nodes.filter((node) => node.type === 'flow' && !node.parentId);

  if (independentFlowNodes.length <= 1) return;

  const unpositionedFlows = independentFlowNodes.filter(
    (node) => node.position.x === 0 && node.position.y === 0,
  );
  const positionedFlows = independentFlowNodes.filter(
    (node) => node.position.x !== 0 || node.position.y !== 0,
  );

  if (unpositionedFlows.length === 0) {
    // Still need to apply header offset to positioned flows' children
    positionedFlows.forEach((flowNode) => {
      const childNodes = nodes.filter((n) => n.parentId === flowNode.id);
      if (childNodes.length > 0 && childNodes[0].position.y < LAYOUT_CONFIG.flowHeaderOffset) {
        childNodes.forEach((child) => {
          child.position.y += LAYOUT_CONFIG.flowHeaderOffset;
        });
      }
    });
    return;
  }

  let currentX = 60; // Start with left padding
  const baseY = 0;

  // Calculate starting X by finding the right edge of any already-positioned flows
  if (positionedFlows.length > 0) {
    positionedFlows.forEach((flowNode) => {
      const childNodes = nodes.filter((n) => n.parentId === flowNode.id);
      let flowWidth = NODE_WIDTH;
      const originalFlowX = flowNode.position.x;

      let minChildX: number | null = null;
      let maxChildRight = originalFlowX;

      if (childNodes.length > 0) {
        childNodes.forEach((child) => {
          const { width: childWidth } = getNodeSize(child, vendors, metaInfoById);
          if (minChildX === null || child.position.x < minChildX) {
            minChildX = child.position.x;
          }
          const childRight = child.position.x + childWidth;
          if (childRight > maxChildRight) {
            maxChildRight = childRight;
          }
        });

        if (minChildX !== null) {
          flowWidth = maxChildRight - minChildX + 60;
        } else {
          flowWidth = maxChildRight - originalFlowX + 60;
        }
      }

      const flowNodeWidth = flowNode.data?.width as number;
      if (flowNodeWidth && flowNodeWidth > flowWidth) {
        flowWidth = flowNodeWidth;
      }

      flowWidth = Math.max(flowWidth, LAYOUT_CONFIG.headerMinWidth);

      if (flowNode.data) {
        flowNode.data.width = flowWidth;
      }

      const flowRight = flowNode.position.x + flowWidth;
      if (flowRight + LAYOUT_CONFIG.independentFlowSpacing > currentX) {
        currentX = flowRight + LAYOUT_CONFIG.independentFlowSpacing;
      }
    });
  }

  // Position unpositioned flows
  unpositionedFlows.forEach((flowNode) => {
    const childNodes = nodes.filter((n) => n.parentId === flowNode.id);

    let minChildX: number | null = null;
    let maxChildRight: number | null = null;
    let flowWidth = NODE_WIDTH;

    if (childNodes.length > 0) {
      childNodes.forEach((child) => {
        const { width: childWidth } = getNodeSize(child, vendors, metaInfoById);
        if (minChildX === null || child.position.x < minChildX) {
          minChildX = child.position.x;
        }
        const childRight = child.position.x + childWidth;
        if (maxChildRight === null || childRight > maxChildRight) {
          maxChildRight = childRight;
        }
      });

      if (minChildX !== null && maxChildRight !== null) {
        flowWidth = maxChildRight - minChildX + 60;
      }
    }

    flowWidth = Math.max(flowWidth, LAYOUT_CONFIG.headerMinWidth);

    const flowNodeX = currentX;
    flowNode.position.x = flowNodeX;
    flowNode.position.y = baseY;

    const flowXOffset = flowNodeX - (minChildX ?? 0);

    if (flowNode.data) {
      flowNode.data.width = flowWidth;
    }

    const ALIGNMENT_TOLERANCE = 10;
    const firstChildCurrentX = childNodes[0]?.position.x ?? 0;
    const isAlreadyAligned = Math.abs(firstChildCurrentX - flowNodeX) <= ALIGNMENT_TOLERANCE;

    if (!isAlreadyAligned && flowXOffset !== 0) {
      childNodes.forEach((child) => {
        child.position.y += LAYOUT_CONFIG.flowHeaderOffset;
      });
    } else {
      childNodes.forEach((child) => {
        if (child.position.y < LAYOUT_CONFIG.flowHeaderOffset) {
          child.position.y += LAYOUT_CONFIG.flowHeaderOffset;
        }
      });
    }

    currentX = flowNode.position.x + flowWidth + LAYOUT_CONFIG.independentFlowSpacing;
  });

  // Apply header offset to positioned flows' children
  positionedFlows.forEach((flowNode) => {
    const childNodes = nodes.filter((n) => n.parentId === flowNode.id);
    if (childNodes.length > 0 && childNodes[0].position.y < LAYOUT_CONFIG.flowHeaderOffset) {
      childNodes.forEach((child) => {
        child.position.y += LAYOUT_CONFIG.flowHeaderOffset;
      });
    }
  });
}

/**
 * Applies Dagre graph layout algorithm to position resource nodes.
 *
 * This is the main layout function that orchestrates all layout steps:
 * 1. Groups nodes by parentId (so nested flows have independent layouts)
 * 2. Applies Dagre layout to each group
 * 3. Adjusts nodes following retriever holders
 * 4. Repositions special nodes (credentials, nexset loaders) relative to their parents
 * 5. Positions secondary flows below the main flow
 * 6. Positions multiple independent flows horizontally
 *
 * Layout Algorithm:
 * - Direction: Left-to-right (LR) - nodes flow from left to right
 * - Spacing: nodesep (horizontal), ranksep (vertical), edgesep (edge separation)
 * - Grouping: Nodes grouped by parentId to maintain nested flow structure
 *
 * Position Preservation:
 * - If a group has no edges, positions are preserved (prevents layout on collapse)
 * - Exception: RAG flows always get layout even without edges
 *
 * Special Node Positioning:
 * - Credential nodes: Positioned relative to their source node
 * - Nexset loader nodes: Positioned to the right of their source node
 * - Secondary flows: Positioned below main flow using deterministic formula
 *
 * @param nodes - Array of all nodes to layout (will be mutated)
 * @param edges - Array of edges connecting nodes
 * @param options - Layout options (isRagFlow, vendors, metaInfoById)
 * @returns The same nodes array with updated positions (mutated in place)
 *
 * @see docs/FlowNodesArchitecture.md - Architecture overview
 * @see docs/FlowNodesModificationGuide.md - How to modify layout spacing
 */
/**
 * Applies per-rank horizontal spacing adjustments for all flow types.
 *
 * This function adjusts the X positions of nodes based on their type,
 * allowing different spacing between different levels of the flow.
 * Uses RANK_SPACING_BY_FLOW_TYPE configuration.
 *
 * @param nodes - All nodes (will be mutated)
 * @param edges - All edges
 * @param options - Layout options
 */
function adjustPerRankSpacing(nodes: Node[], edges: Edge[], options: DagreLayoutOptions): void {
  const layoutDir = rankdirToLayoutDirection(options.rankdir);
  const rankSpacingConfig = getRankSpacingForFlowType(options.flowType, layoutDir);
  if (!rankSpacingConfig) return;

  const baseRanksep = getDagreSpacingForFlowType(options.flowType, layoutDir).ranksep;

  // Build a map of node relationships to determine rank order
  const nodeById = new Map(nodes.map((n) => [n.id, n]));

  // Find root nodes (nodes with no incoming edges from OTHER RESOURCE nodes)
  const resourceNodes = nodes.filter((n) => isResourceNode(n));
  const resourceNodeIds = new Set(resourceNodes.map((n) => n.id));

  // Only consider edges between resource nodes for determining root nodes
  const resourceEdges = edges.filter(
    (e) => resourceNodeIds.has(e.source) && resourceNodeIds.has(e.target),
  );
  const targetNodeIds = new Set(resourceEdges.map((e) => e.target));
  const rootNodes = resourceNodes.filter((n) => !targetNodeIds.has(n.id));

  if (rootNodes.length === 0) return;

  // Calculate rank (horizontal level) for each node using BFS from root nodes
  const nodeRanks = new Map<string, number>();

  // Initialize with root nodes at rank 0
  const queue: { nodeId: string; rank: number }[] = [];
  rootNodes.forEach((n) => {
    nodeRanks.set(n.id, 0);
    queue.push({ nodeId: n.id, rank: 0 });
  });

  // BFS to assign ranks (only traverse resource edges)
  while (queue.length > 0) {
    const { nodeId, rank } = queue.shift()!;

    // Find all resource edges from this node
    const outgoingEdges = resourceEdges.filter((e) => e.source === nodeId);
    outgoingEdges.forEach((edge) => {
      const targetNode = nodeById.get(edge.target);
      if (targetNode && !nodeRanks.has(edge.target)) {
        nodeRanks.set(edge.target, rank + 1);
        queue.push({ nodeId: edge.target, rank: rank + 1 });
      }
    });
  }

  // Group nodes by rank
  const nodesByRank = new Map<number, Node[]>();
  nodes.forEach((n) => {
    const rank = nodeRanks.get(n.id);
    if (rank !== undefined) {
      if (!nodesByRank.has(rank)) {
        nodesByRank.set(rank, []);
      }
      nodesByRank.get(rank)!.push(n);
    }
  });

  const rankValues = Array.from(nodeRanks.values());
  if (rankValues.length === 0) return;

  // Calculate cumulative X offset for each rank
  const maxRank = Math.max(...rankValues);
  const rankXOffsets = new Map<number, number>();
  let cumulativeOffset = 0;

  for (let rank = 0; rank <= maxRank; rank++) {
    rankXOffsets.set(rank, cumulativeOffset);

    // Determine spacing after this rank based on node types in this rank.
    // Use the max of configured values (still respects the "widest wins"
    // policy when a rank has mixed node types) but allow negatives so
    // the table can pull ranks CLOSER than dagre's default.
    const nodesInRank = nodesByRank.get(rank) || [];
    let maxSpacingForRank: number | null = null;

    nodesInRank.forEach((n) => {
      const nodeType = n.type || '';
      const configured = rankSpacingConfig[nodeType];
      let spacing = typeof configured === 'number' ? configured : null;
      if (nodeType === 'data_sink') {
        const hasWebhookResponseEdge = resourceEdges.some(
          (e) =>
            e.source === n.id &&
            (e as Edge & { data?: { isWebhookEdge?: boolean } }).data?.isWebhookEdge,
        );
        if (hasWebhookResponseEdge) {
          spacing = DATA_SINK_WEBHOOK_RANK_SPACING;
        }
      }
      if (spacing !== null) {
        maxSpacingForRank =
          maxSpacingForRank === null ? spacing : Math.max(maxSpacingForRank, spacing);
      }
    });

    // Apply delta from dagre's base ranksep (negative = tighter).
    if (maxSpacingForRank !== null) {
      cumulativeOffset += maxSpacingForRank - baseRanksep;
    }
  }

  // Apply offsets along the main axis (X for LR, Y for TB)
  const isTB = options.rankdir === 'TB';
  nodes.forEach((n) => {
    const rank = nodeRanks.get(n.id);
    if (rank !== undefined && rank > 0) {
      const offset = rankXOffsets.get(rank) || 0;
      if (isTB) {
        n.position.y += offset;
      } else {
        n.position.x += offset;
      }
    }
  });
}

/**
 * Post-processes API Server flow layout to align RESULT/ERROR nodes with TARGET handles.
 *
 * The TARGET node has SUCCESS and FAIL handles close together (4px gap).
 * This function adjusts RESULT and ERROR node positions to align with those handles.
 *
 * @param nodes - All nodes (will be mutated)
 * @param edges - All edges
 * @param options - Layout options
 */
function adjustApiServerBranchingLayout(
  nodes: Node[],
  edges: Edge[],
  options: DagreLayoutOptions,
): void {
  if (options.flowType !== 'api_server') return;

  const { vendors, metaInfoById } = options;

  // Find all TARGET nodes
  const targetNodes = nodes.filter((n) => n.type === 'api_target');

  targetNodes.forEach((targetNode) => {
    const targetId = targetNode.id;
    const targetSize = getNodeSize(targetNode, vendors, metaInfoById);

    // Find edges from this TARGET's success and fail handles
    const successEdge = edges.find((e) => e.source === targetId && e.sourceHandle === 'success');
    const failEdge = edges.find((e) => e.source === targetId && e.sourceHandle === 'fail');

    // Find the connected RESULT and ERROR nodes
    const resultNode = successEdge ? nodes.find((n) => n.id === successEdge.target) : null;
    const errorNode = failEdge ? nodes.find((n) => n.id === failEdge.target) : null;

    if (!resultNode && !errorNode) return;

    const isTB = options.rankdir === 'TB';

    if (isTB) {
      // TB: spread RESULT and ERROR horizontally around TARGET center
      const targetCenterX = targetNode.position.x + targetSize.width / 2;
      const horizontalSpreadResult = 60;
      const horizontalSpreadError = 100;

      if (resultNode) {
        const resultSize = getNodeSize(resultNode, vendors, metaInfoById);
        resultNode.position.x = targetCenterX - horizontalSpreadResult - resultSize.width / 2;
      }
      if (errorNode) {
        const errorSize = getNodeSize(errorNode, vendors, metaInfoById);
        errorNode.position.x = targetCenterX + horizontalSpreadError - errorSize.width / 2;
      }
    } else {
      // LR: spread RESULT and ERROR vertically around TARGET center
      const targetCenterY = targetNode.position.y + targetSize.height / 2;
      const verticalSpreadResult = 60;
      const verticalSpreadError = 100;

      if (resultNode) {
        const resultSize = getNodeSize(resultNode, vendors, metaInfoById);
        resultNode.position.y = targetCenterY - verticalSpreadResult - resultSize.height / 2;
      }
      if (errorNode) {
        const errorSize = getNodeSize(errorNode, vendors, metaInfoById);
        errorNode.position.y = targetCenterY + verticalSpreadError - errorSize.height / 2;
      }
    }
  });
}

export function applyDagreLayoutToResources(
  nodes: Node[],
  edges: Edge[],
  options: DagreLayoutOptions,
): Node[] {
  const layoutDir = rankdirToLayoutDirection(options.rankdir);
  const spacing = getDagreSpacingForFlowType(options.flowType, layoutDir);
  const ranksep = Math.round(spacing.ranksep);

  // STEP 1: Group nodes by parentId and apply Dagre layout to each group
  const groups = groupNodesByParentId(nodes);
  Object.entries(groups).forEach(([parentId, groupNodes]) => {
    applyDagreLayoutToGroup(groupNodes, parentId, nodes, edges, {
      ...options,
      nodesep: spacing.nodesep,
      ranksep,
      edgesep: spacing.edgesep,
    });
  });

  // STEP 1.5: Apply per-rank horizontal spacing adjustments
  adjustPerRankSpacing(nodes, edges, options);

  // STEP 1.6: For API Server flows, adjust RESULT/ERROR positioning for handle alignment
  adjustApiServerBranchingLayout(nodes, edges, options);

  // STEP 2: Adjust nodes following retriever holders for fixed edge length
  adjustNodesAfterRetrieverHolders(nodes, options);

  // STEP 3: Reposition special nodes (credentials and nexset loaders)
  repositionCredentialNodes(nodes, options);
  repositionNexsetLoaderNodes(nodes, options);
  adjustSharedDatasetPositions(nodes, edges, { rankdir: options.rankdir });

  // STEP 4: Position secondary flows below their parent flows
  positionSecondaryFlows(nodes, edges);

  // STEP 5: Position multiple independent flows horizontally
  positionIndependentFlows(nodes, options);

  // STEP 5.5: Position flow control nodes (Run Now, Cron) relative to their flow
  positionFlowControlNodes(nodes, options);

  // STEP 5.6: In TB, ensure the top-most node in each flow sits below
  // the flow-summary overlay header. Schedule canvas nodes (cron/run-now)
  // sit above the source and would otherwise clip under the overlay.
  ensureTopPaddingPerFlow(nodes, options);

  // STEP 6: Apply handle positions and return new node references (immutability)
  // New refs ensure React Flow re-renders when layout direction changes.
  // sourcePosition/targetPosition align edges with the chosen rankdir (LR vs RL).
  const rankdir = options.rankdir ?? LAYOUT_CONFIG.defaultRankdir;
  const { sourcePosition, targetPosition } = getHandlePositionsForRankDir(rankdir);

  return nodes.map((node) => ({
    ...node,
    sourcePosition,
    targetPosition,
  }));
}
